<script setup lang="ts">
import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { HistogramSpec } from "../sbi/protocol";
import { baseOption } from "./echarts-base";
import { categoryLabelPolicy, plotWidthEstimate } from "./chart-layout";
import { useChartLayout } from "./use-chart-layout";

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

const props = defineProps<{ spec: HistogramSpec }>();

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

interface BinModel {
  centres: number[];
  counts: number[];
  // Lower / upper edges per bin. Used to synthesise the click datum
  // (`{ bin_lo, bin_hi, count }`) so authors can `WHERE col BETWEEN ...`
  // on the bin range without having to re-derive it.
  los: number[];
  his: number[];
}

const bins = computed<BinModel>(() => {
  const n = Math.max(2, Math.min(props.spec.bins ?? 20, 100));
  // `spec.data` is a `ChartData` union (rows-array | SqlMarker |
  // DeriveMarker). The InsightChart dispatcher only mounts this
  // subcomponent after the marker has resolved — narrow with
  // `Array.isArray` so a defective dispatch path can't crash render.
  const rows = Array.isArray(props.spec.data) ? props.spec.data : [];
  const values = rows
    .map((row) => Number(row[props.spec.field]))
    .filter((v) => Number.isFinite(v));
  if (values.length === 0) return { centres: [], counts: [], los: [], his: [] };

  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) {
    return { centres: [min], counts: [values.length], los: [min], his: [min] };
  }

  const width = (max - min) / n;
  const counts = new Array<number>(n).fill(0);
  for (const v of values) {
    const idx = Math.min(n - 1, Math.floor((v - min) / width));
    counts[idx] += 1;
  }
  const centres = counts.map((_, i) => +(min + width * (i + 0.5)).toPrecision(4));
  const los = counts.map((_, i) => min + width * i);
  const his = counts.map((_, i) => min + width * (i + 1));
  return { centres, counts, los, his };
});

const option = computed(() => {
  const base = baseOption({ title: props.spec.title }, ctx.value);
  return {
    ...base,
    xAxis: {
      ...base.xAxis,
      type: "category",
      data: bins.value.centres,
      // Up to 100 bins: let ECharts thin the bin-centre labels rather
      // than overlap them.
      axisLabel: {
        ...base.xAxis.axisLabel,
        ...categoryLabelPolicy({
          labels: bins.value.centres,
          plotWidth: plotWidthEstimate(ctx.value.width),
          mode: "thin",
        }),
      },
    },
    yAxis: { ...base.yAxis, type: "value" },
    series: [{ type: "bar", barCategoryGap: 0, data: bins.value.counts }],
    legend: { show: false },
  };
});

const height = computed(() =>
  resolveChartHeight("histogram", props.spec.height),
);

// Histogram bars are synthesised by the host (the input rows are
// pre-binning), so unlike the other charts we synthesise the datum:
// `{ bin_lo, bin_hi, count }`. Authors typically use this to filter
// the underlying data with `WHERE field BETWEEN bin_lo AND bin_hi`.
function onClick(p: { dataIndex: number }) {
  if (!props.spec.id) return;
  if (!p || typeof p.dataIndex !== "number") return;
  const i = p.dataIndex;
  const lo = bins.value.los[i];
  const hi = bins.value.his[i];
  const count = bins.value.counts[i];
  if (lo === undefined || hi === undefined || count === undefined) return;
  emit("click-target", props.spec.id, { bin_lo: lo, bin_hi: hi, count });
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
